// PayrollRunPage — admin monthly payroll RUN (Phase A2/A3).
//
// Attendance-driven draft → review → finalise, with data-confidence flags that
// must be acknowledged before a month can be locked, and two CA exports. This is
// a NEW admin screen; the legacy PayrollPage (period generate/list) is untouched.
// It drives /api/payroll/run/* only.

import React, { useState, useCallback } from "react";
import { useToast } from "@chakra-ui/react";
import { api, API_BASE } from "../../lib/api";
import { useShellHeader } from "../../Components/AppShell";
import { Card, TONE_VARS } from "../../Components/ui";
import { Backdrop, Modal, Header, Body, Footer, Field, inputStyle, btnPrimary as mBtnPrimary, btnGhost as mBtnGhost } from "../Leaves/ApplyLeaveModal";

// Shared tones (Components/ui/statusTones.js) — these flags render in the
// normal themed run table, so they need to follow the theme.
const FLAG_STYLE = {
  block: TONE_VARS.danger,
  warn:  TONE_VARS.warning,
};

const money = (n) => (n == null ? "—" : `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function PayrollRunPage() {
  const toast = useToast();
  useShellHeader({ title: "Monthly Payroll Run", breadcrumb: "Admin · Payroll" }, []);

  const [month, setMonth] = useState(currentMonth());
  const [run, setRun] = useState(null); // { month, lines }
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [override, setOverride] = useState(null); // line being overridden

  const isFinalised = run?.month?.status === "finalised";

  const compute = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api("/payroll/run/recompute", { method: "POST", body: JSON.stringify({ month }) });
      setRun(r);
      toast({ title: "Draft computed.", status: "success" });
    } catch (e) {
      toast({ title: e.data?.message || e.message, status: "error" });
    } finally { setLoading(false); }
  }, [month, toast]);

  async function reload() {
    if (!run?.month?._id) return;
    try { setRun(await api(`/payroll/run/${run.month._id}`)); } catch (e) { toast({ title: e.message, status: "error" }); }
  }

  async function acknowledge(line) {
    const note = window.prompt(`Acknowledge the flags for ${line.staffName}?\nThis confirms you've reviewed the risks below.\n\nOptional note:`);
    if (note === null) return;
    try {
      await api(`/payroll/run/${run.month._id}/line/${line._id}/acknowledge`, { method: "POST", body: JSON.stringify({ note: note || "" }) });
      toast({ title: "Acknowledged.", status: "success" });
      reload();
    } catch (e) { toast({ title: e.data?.message || e.message, status: "error" }); }
  }

  async function clearOverride(line) {
    const reason = window.prompt("Reason for clearing this override:");
    if (!reason) { if (reason !== null) toast({ title: "A reason is required.", status: "warning" }); return; }
    try {
      await api(`/payroll/run/${run.month._id}/line/${line._id}/override`, { method: "DELETE", body: JSON.stringify({ reason }) });
      toast({ title: "Override cleared.", status: "success" });
      reload();
    } catch (e) { toast({ title: e.data?.message || e.message, status: "error" }); }
  }

  async function finalise() {
    if (!window.confirm("Finalise and lock this month's payroll? Attendance for the month can't change until it's unlocked.")) return;
    setBusy(true);
    try {
      await api(`/payroll/run/${run.month._id}/finalise`, { method: "POST", body: JSON.stringify({}) });
      toast({ title: "Payroll finalised and locked.", status: "success" });
      reload();
    } catch (e) {
      const d = e.data;
      if (d?.blockedStaff?.length) {
        toast({ title: d.message, description: d.blockedStaff.map((b) => b.staff).join(", "), status: "error", duration: 8000 });
      } else {
        toast({ title: d?.message || e.message, status: "error" });
      }
    } finally { setBusy(false); }
  }

  async function unlock() {
    const reason = window.prompt("Reason for unlocking this finalised month:");
    if (!reason) { if (reason !== null) toast({ title: "A reason is required.", status: "warning" }); return; }
    setBusy(true);
    try {
      await api(`/payroll/run/${run.month._id}/unlock`, { method: "POST", body: JSON.stringify({ reason }) });
      toast({ title: "Month unlocked.", status: "success" });
      reload();
    } catch (e) { toast({ title: e.data?.message || e.message, status: "error" }); }
    finally { setBusy(false); }
  }

  function download(kind) {
    const token = localStorage.getItem("token");
    const url = `${API_BASE}/payroll/run/${run.month._id}/export/${kind}.xlsx`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.blob() : Promise.reject(r))
      .then((blob) => {
        const u = URL.createObjectURL(blob);
        const a = document.createElement("a");
        const label = `${run.month.year}-${String(run.month.month).padStart(2, "0")}`;
        a.href = u; a.download = `Payroll_${label}_${kind}.xlsx`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(u);
      })
      .catch(() => toast({ title: "Could not export. Try again.", status: "error" }));
  }

  const totals = run?.month?.totals;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: "var(--text-primary)", margin: 0 }}>Monthly Payroll Run</h2>
        <div style={{ fontSize: 13, color: "var(--text-tertiary)", marginTop: 4, maxWidth: 720 }}>
          Builds each employee's payable amount from their recorded attendance. Any working day with no attendance and no leave is flagged — so a genuinely-worked day is never silently paid as absent. Review, override where needed, acknowledge the flags, then finalise for the CA.
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <label style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-tertiary)", fontWeight: 600 }}>Month:</label>
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} style={{ ...inputStyle, width: 180 }} />
        <button onClick={compute} style={btnPrimary} disabled={loading}>{loading ? "Computing…" : "Compute draft"}</button>
      </div>

      {run && (
        <>
          <Card>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 20, alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                <Stat label="Status" value={<StatusPill status={run.month.status} />} />
                <Stat label="Employees" value={totals?.staffCount ?? 0} />
                <Stat label="Gross" value={money(totals?.gross)} />
                <Stat label="Payable" value={money(totals?.payable)} />
                <Stat label="Needs review" value={<span style={{ color: totals?.flaggedCount ? "var(--tone-danger-fg)" : "var(--tone-success-fg)", fontWeight: 700 }}>{totals?.flaggedCount ?? 0}</span>} />
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button onClick={() => download("summary")} style={btnGhost}>📥 Summary</button>
                <button onClick={() => download("detail")} style={btnGhost}>📥 Day detail</button>
                {!isFinalised ? (
                  <button onClick={finalise} style={btnFinalise} disabled={busy || (totals?.flaggedCount ?? 0) > 0}>
                    {(totals?.flaggedCount ?? 0) > 0 ? `Resolve ${totals.flaggedCount} flag(s) to finalise` : "Finalise & lock"}
                  </button>
                ) : (
                  <button onClick={unlock} style={btnUnlock} disabled={busy}>Unlock</button>
                )}
              </div>
            </div>
            {run.month.roundingNote && (
              <div style={{ marginTop: 12, fontSize: 11, color: "var(--text-tertiary)", lineHeight: 1.5 }}>{run.month.roundingNote}</div>
            )}
          </Card>

          <Card padding="none">
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
                <thead><tr>
                  {["Employee", "Work", "Worked", "Paid Lv", "Unpaid", "Absent", "LOP", "Computed", "Final", "Flags", "Actions"].map((h) => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {run.lines.length === 0 ? (
                    <tr><td colSpan={11} style={{ ...tdBase, textAlign: "center", color: "var(--text-tertiary)", padding: "24px 12px" }}>No lines.</td></tr>
                  ) : run.lines.map((l) => {
                    const overridden = l.overridePayable != null || l.overridePaidDays != null;
                    const hasBlock = (l.flags || []).some((f) => f.severity === "block");
                    return (
                      <tr key={l._id} style={{ borderTop: "1px solid var(--border-subtle)", background: hasBlock && !l.acknowledged ? "rgba(254,226,226,0.35)" : undefined }}>
                        <td style={tdBase}>
                          <div style={{ fontWeight: 500 }}>{l.staffName}</div>
                          <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>{l.staffEmail}{l.employmentType ? ` · ${l.employmentType}` : ""}</div>
                        </td>
                        <td className="tabular" style={tdBase}>{l.workingDays}</td>
                        <td className="tabular" style={tdBase}>{l.workedDays}</td>
                        <td className="tabular" style={tdBase}>{l.paidLeaveDays}</td>
                        <td className="tabular" style={tdBase}>{l.unpaidLeaveDays}</td>
                        <td className="tabular" style={{ ...tdBase, color: l.absentDays ? "var(--tone-danger-fg)" : undefined, fontWeight: l.absentDays ? 700 : 400 }}>{l.absentDays}</td>
                        <td className="tabular" style={tdBase}>{l.lopDays}</td>
                        <td className="tabular" style={tdBase}>{money(l.computedPayable)}</td>
                        <td className="tabular" style={{ ...tdBase, fontWeight: 700 }}>
                          {money(l.finalPayable)}
                          {overridden && <span title={l.overrideReason} style={{ marginLeft: 6, fontSize: 10, color: "var(--tone-info-fg)", fontWeight: 700 }}>(ovr)</span>}
                        </td>
                        <td style={{ ...tdBase, maxWidth: 260 }}>
                          {(l.flags || []).length === 0 ? <span style={{ color: "var(--tone-success-fg)" }}>✓</span> : (
                            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                              {l.flags.map((f, i) => {
                                const st = FLAG_STYLE[f.severity] || FLAG_STYLE.warn;
                                return <span key={i} title={f.message} style={{ background: st.bg, color: st.co, border: `1px solid ${st.border}`, padding: "1px 7px", borderRadius: 10, fontSize: 10, fontWeight: 700, width: "fit-content" }}>{f.code}</span>;
                              })}
                              {l.acknowledged && <span style={{ fontSize: 10, color: "var(--tone-success-fg)", fontWeight: 600 }}>✓ acknowledged</span>}
                            </div>
                          )}
                        </td>
                        <td style={tdBase}>
                          {!isFinalised && (
                            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                              <button style={btnMini} onClick={() => setOverride(l)}>Override</button>
                              {overridden && <button style={btnMini} onClick={() => clearOverride(l)}>Clear</button>}
                              {(l.flags || []).length > 0 && !l.acknowledged && <button style={btnMiniWarn} onClick={() => acknowledge(l)}>Acknowledge</button>}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {override && (
        <OverrideModal
          line={override}
          onClose={() => setOverride(null)}
          onDone={() => { setOverride(null); reload(); }}
          monthId={run.month._id}
        />
      )}
    </div>
  );
}

function OverrideModal({ line, onClose, onDone, monthId }) {
  const toast = useToast();
  const [mode, setMode] = useState("amount"); // amount | days
  const [amount, setAmount] = useState(line.finalPayable != null ? String(line.finalPayable) : "");
  const [paidDays, setPaidDays] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!reason.trim()) { toast({ title: "A reason is required.", status: "warning" }); return; }
    const body = { reason };
    if (mode === "amount") {
      if (amount === "" || Number(amount) < 0) { toast({ title: "Enter a valid amount.", status: "warning" }); return; }
      body.amount = Number(amount);
    } else {
      if (paidDays === "" || Number(paidDays) < 0) { toast({ title: "Enter a valid paid-day count.", status: "warning" }); return; }
      body.paidDays = Number(paidDays);
    }
    setBusy(true);
    try {
      await api(`/payroll/run/${monthId}/line/${line._id}/override`, { method: "POST", body: JSON.stringify(body) });
      toast({ title: "Override saved.", status: "success" });
      onDone && onDone();
    } catch (e) { toast({ title: e.data?.message || e.message, status: "error" }); }
    finally { setBusy(false); }
  }

  return (
    <Backdrop onClose={onClose}>
      <Modal>
        <Header title={`Override pay — ${line.staffName}`} onClose={onClose} />
        <Body>
          <div style={{ fontSize: 12, color: "var(--text-tertiary)", marginBottom: 12 }}>
            Computed: <strong>{money(line.computedPayable)}</strong> · Working days {line.workingDays} · LOP {line.lopDays}. The original computed value is always preserved.
          </div>
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <button onClick={() => setMode("amount")} style={mode === "amount" ? tabOn : tabOff}>Set amount (₹)</button>
            <button onClick={() => setMode("days")} style={mode === "days" ? tabOn : tabOff}>Set paid days</button>
          </div>
          {mode === "amount" ? (
            <Field label="PAYABLE AMOUNT (₹) *"><input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} style={inputStyle} /></Field>
          ) : (
            <Field label={`PAID DAYS (0 – ${line.workingDays}) *`}><input type="number" min="0" max={line.workingDays} step="0.5" value={paidDays} onChange={(e) => setPaidDays(e.target.value)} style={inputStyle} /></Field>
          )}
          <Field label="REASON *">
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is this line being overridden? (audited)" style={{ ...inputStyle, minHeight: 72, resize: "vertical" }} />
          </Field>
        </Body>
        <Footer>
          <button onClick={onClose} style={mBtnGhost} disabled={busy}>Cancel</button>
          <button onClick={submit} style={mBtnPrimary} disabled={busy}>{busy ? "Saving…" : "Save override"}</button>
        </Footer>
      </Modal>
    </Backdrop>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-tertiary)", fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: "var(--text-primary)", marginTop: 2 }}>{value}</div>
    </div>
  );
}
function StatusPill({ status }) {
  const on = status === "finalised";
  return <span style={{ background: on ? "var(--tone-success-bg)" : "var(--tone-info-bg)", color: on ? "var(--tone-success-fg)" : "var(--tone-info-fg)", padding: "2px 10px", borderRadius: 12, fontSize: 12, fontWeight: 700, textTransform: "uppercase" }}>{on ? "Finalised" : "Draft"}</span>;
}

const thStyle = {
  textAlign: "left", padding: "10px 12px", fontSize: 11, fontWeight: 600,
  textTransform: "uppercase", letterSpacing: "0.05em",
  color: "var(--text-tertiary)", background: "var(--bg-subtle)",
  borderBottom: "1px solid var(--border-subtle)", whiteSpace: "nowrap",
};
const tdBase = { padding: "9px 12px", color: "var(--text-primary)", verticalAlign: "top" };
const btnPrimary = { background: "var(--accent-primary)", color: "white", border: "none", padding: "8px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" };
const btnGhost = { background: "transparent", color: "var(--text-secondary)", padding: "8px 12px", border: "1px solid var(--border-default)", borderRadius: 8, fontSize: 12, fontWeight: 500, cursor: "pointer" };
const btnFinalise = { background: "var(--fill-success)", color: "white", border: "none", padding: "8px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" };
const btnUnlock = { background: "var(--fill-warning)", color: "white", border: "none", padding: "8px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" };
const btnMini = { background: "transparent", color: "var(--text-secondary)", padding: "4px 10px", border: "1px solid var(--border-default)", borderRadius: 6, fontSize: 12, cursor: "pointer" };
const btnMiniWarn = { background: "var(--fill-danger)", color: "white", padding: "4px 10px", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer" };
const tabOn = { background: "var(--accent-primary)", color: "white", border: "none", padding: "6px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer" };
const tabOff = { background: "transparent", color: "var(--text-secondary)", border: "1px solid var(--border-default)", padding: "6px 12px", borderRadius: 6, fontSize: 12, cursor: "pointer" };
